import React from 'react';
import { motion } from 'framer-motion';
import CardBackground from './CardBackground';

interface ModernCardProps {
  title: string;
  content: string;
  color?: string;
  link?: string | null;
  onClick?: () => void;
  date?: string;
  category?: string;
}

const ModernCard: React.FC<ModernCardProps> = ({ 
  title, 
  content, 
  color = '#1a1a1a', 
  link = null,
  onClick,
  date,
  category
}) => {
  // Create a pattern for the card background if no Three.js
  const fallbackPattern = {
    backgroundImage: `radial-gradient(circle at 10% 20%, ${color}66 0%, ${color}22 70%)`,
    backgroundSize: 'cover'
  };

  return (
    <motion.div
      className="modern-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ 
        scale: 1.03,
        boxShadow: "0 15px 25px -5px rgba(0, 0, 0, 0.2)"
      }}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Fallback Background */}
      <div className="card-background" style={fallbackPattern}></div>
      
      {/* Three.js Background */}
      <div className="card-background">
        <CardBackground color={color} />
      </div>
      
      {/* Overlay for better readability */}
      <div className="card-overlay"></div>
      
      {/* Category Line */}
      {category && (
        <div 
          className="card-category-line"
          style={{ backgroundColor: color }}
        ></div>
      )}
      
      {/* Content */}
      <div className="card-content">
        <div className="flex justify-between items-start mb-2">
          {date && (
            <span className="card-tag">
              {date}
            </span>
          )}
          
          {category && (
            <span 
              className="card-tag"
              style={{ borderLeftColor: color }}
            >
              {category}
            </span>
          )}
        </div>
        
        <h3 className="card-title text-shadow">{title}</h3>
        <p className="card-excerpt text-shadow">{content}</p>
        
        <div className="mt-auto">
          {link ? (
            <motion.a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="card-action"
              whileHover={{ scale: 1.05 }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(link, "_blank");
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span>View on X</span>
            </motion.a>
          ) : (
            <motion.div
              className="card-action"
              whileHover={{ scale: 1.05 }}
            >
              <span>Read full story</span>
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Street Fighter-like diagonal accent */}
      <div className="card-accent"></div>
    </motion.div>
  );
};

export default ModernCard;